import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Report, ReportStatus, ReportRequest, Customer, Technician, Attachment, AttachmentRequest, UserInfo, SignatureMethod, TravelTimeData, ServiceLocation } from '../types';
import { getFailureDisplayItems, getUnclassifiedFailureLabel } from '../utils/failureClassification';
import { apiClient } from '../services/apiClient';
import { authService } from '../services/authService';
import { useAgent } from '../hooks/useAgent';
import { mapReportToUbaldoDTO } from '../utils/ubaldoMapper';
import { generateReportPdf, downloadBlob } from '../services/ubaldoService';
import { useNotification } from '../hooks/useNotification';
import { useConfirm } from '../hooks/useConfirm';
import { useReport } from '../hooks/useReport';
import { useCustomer } from '../hooks/useCustomer';
import { useAttachments } from '../hooks/useAttachments';
import { useNextReportNumber } from '../hooks/useNextReportNumber';
import { useForeignAgentReadonly } from '../hooks/useForeignAgentReadonly';
import CustomerSelectionModal from '../components/forms/CustomerSelectionModal';
import TechnicianSelectionModal from '../components/forms/TechnicianSelectionModal';
import SignatureModal from '../components/SignatureModal';
import { CustomerInfo, ServiceLocationSection } from '../components/sidebar';
import { BillingStatusBadge } from '../components/BillingStatusBadge';
import { FormHeader } from '../components/FormHeader';
import { BreadcrumbItem } from '../components/Breadcrumb';
import { StatusFlowTooltip } from '../components/StatusFlowTooltip';
import { SIGNATURE_METHOD } from '../config/signatureConfig';
import { validateAndPrepareFile, isImageFile, isImageFileName, isZipFileName, getMimeTypeFromFileName, prepareSignatureImage, dataUrlToFile, fileToBase64Content } from '../utils/fileUtils';
import ImagePreviewModal from '../components/ImagePreviewModal';
import MultiMachineCsvImportModal from '../components/forms/MultiMachineCsvImportModal';
import PantheraImportModal from '../components/forms/PantheraImportModal';
import CsvImportTooltip from '../components/forms/CsvImportTooltip';
import InfoTooltip from '../components/InfoTooltip/InfoTooltip';
import TravelTimeSection from '../components/forms/TravelTimeSection';
import QrSignatureModal from '../components/QrSignatureModal';
import { generateSignatureToken } from '../services/signatureTokenService';
import { calculateReportTotals } from '../utils/calculateReportTotals';
import { validateTravelTimeForClose } from '../utils/travelTimeValidation';
import { formatMinutesToHoursMinutes } from '../utils/timeUtils';
import { getDashboardPath } from '../utils/navigationUtils';
import './ReportForm.css';

const serializeReportForDirtyCheck = (r: ReportFormState): string =>
  JSON.stringify({
    number: r.number,
    creation: r.creation,
    customerId: r.customerId,
    technicianId: r.technicianId,
    supportTechnicianId: r.supportTechnicianId ?? null,
    travelTime: r.travelTime ?? 0,
    travelKm: r.travelKm ?? 0,
    workTime: r.workTime ?? 0,
    startTime: r.startTime,
    finishTime: r.finishTime,
    travelTimeData: r.travelTimeData ?? {},
    serviceLocation: r.serviceLocation ?? {},
  });

interface ReportFormState {
  id?: string;
  number?: string;
  creation?: string;
  status?: string;
  agentId?: string;
  customerId?: string;
  customerName?: string;
  technicianId?: string;
  technicianName?: string;
  supportTechnicianId?: string;
  supportTechnicianName?: string;
  travelTime?: number;
  travelKm?: number;
  workTime?: number;
  startTime?: string;
  finishTime?: string;
  travelTimeData?: TravelTimeData;
  serviceLocation?: ServiceLocation;
  machines?: Report['machines'];
  attachments?: Report['attachments'];
  billingStatus?: string;
  // Temporary fields for selection
  selectedCustomer?: Customer;
  selectedTechnician?: Technician;
  selectedSupportTechnician?: Technician;
}

const ReportForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { showSuccess, showError, NotificationContainer } = useNotification();
  const { confirm, ConfirmDialog } = useConfirm();
  const isEditMode = id !== 'new';

  const reportId = isEditMode && id ? id : undefined;
  const { report: loadedReport, loading: reportLoading, error: reportError, refresh: refreshReport, updateCache: updateReportCache } = useReport(reportId);

  // Get current user for agentId
  const [currentUser] = useState<UserInfo | null>(() => authService.getCurrentUser());

  const [report, setReport] = useState<ReportFormState>({
    travelTime: 0,
    travelKm: 0,
    workTime: 0,
    startTime: new Date().toISOString(),
    finishTime: new Date().toISOString(),
    travelTimeData: {},
    machines: []
  });

  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showTechnicianModal, setShowTechnicianModal] = useState(false);
  const [showSupportTechnicianModal, setShowSupportTechnicianModal] = useState(false);
  const [showMultiMachineCsvModal, setShowMultiMachineCsvModal] = useState(false);
  const [showPantheraImportModal, setShowPantheraImportModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showCustomerSignatureModal, setShowCustomerSignatureModal] = useState(false);
  const [showTechnicianSignatureModal, setShowTechnicianSignatureModal] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [qrSignatureToken, setQrSignatureToken] = useState<{ token: string; expiresAt: string } | null>(null);

  // Agent for the logo used in PDF generation
  const reportAgentId = report.agentId || currentUser?.agentId;
  const { agent } = useAgent(isEditMode ? reportAgentId : undefined);

  // Compute agentId for next report number: user's agentId → report's agentId → customer's agentId
  const effectiveAgentId = React.useMemo(() => {
    if (isEditMode) return undefined;
    return currentUser?.agentId || report.agentId || report.selectedCustomer?.agentId;
  }, [isEditMode, currentUser?.agentId, report.agentId, report.selectedCustomer?.agentId]);

  // In edit mode, fetch customer details via SWR (deduplication with useForeignAgentReadonly).
  // In new mode, the customer comes from report.selectedCustomer (set by the modal).
  const { customer: editModeCustomer } = useCustomer(isEditMode ? loadedReport?.customerId : undefined);

  // True when the logged-in user is ADMIN GLOBAL and the report belongs to a different agent.
  // In that case no write operation is allowed (fully read-only).
  // Pass undefined in new mode so the check is never triggered for unsaved reports.
  const isReadonlyForForeignAgent = useForeignAgentReadonly(isEditMode ? loadedReport?.customerId : undefined);

  const { nextNumber } = useNextReportNumber(effectiveAgentId);

  // Attachments management
  const { attachments, loading: attachmentsLoading, mutate: mutateAttachments } = useAttachments(reportId);
  const lastSavedRef = useRef<string | null>(null);
  const customerSignFileRef = useRef<HTMLInputElement>(null);
  const technicianSignFileRef = useRef<HTMLInputElement>(null);
  const otherFileRef = useRef<HTMLInputElement>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<Attachment | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);
  const [otherNote, setOtherNote] = useState('');

  const loading = React.useMemo(
    () => isEditMode && (reportLoading || attachmentsLoading),
    [isEditMode, reportLoading, attachmentsLoading]
  );

  // Separate signatures from other attachments
  // The latest customer signature wins: its updatedAt is the signing date printed on the PDF report
  const customerSignature = React.useMemo(
    () => attachments
      .filter(a => a.note === 'customer_sign' || a.fileName?.toLowerCase().includes('customer'))
      .reduce<Attachment | undefined>(
        (latest, a) => (!latest || Date.parse(a.updatedAt || a.createdAt) > Date.parse(latest.updatedAt || latest.createdAt) ? a : latest),
        undefined
      ),
    [attachments]
  );

  const technicianSignature = React.useMemo(
    () => attachments.find(a => a.note === 'technician_sign' || a.fileName?.toLowerCase().includes('technician')),
    [attachments]
  );

  const otherAttachments = React.useMemo(
    () => attachments.filter(a =>
      a.note !== 'customer_sign' &&
      a.note !== 'technician_sign' &&
      !a.fileName?.toLowerCase().includes('customer') &&
      !a.fileName?.toLowerCase().includes('technician')
    ),
    [attachments]
  );

  useEffect(() => {
    if (reportError || (isEditMode && !reportLoading && !loadedReport)) {
      showError(t('messages.errorLoadingReport'));
      navigate('/');
    }
  }, [loadedReport, reportLoading, reportError, isEditMode, showError, t, navigate]);

  useEffect(() => {
    if (loadedReport) {
      const newState = { ...loadedReport, selectedCustomer: undefined, selectedTechnician: undefined };
      setReport(newState);
      lastSavedRef.current = serializeReportForDirtyCheck(newState);
    }
  }, [loadedReport]);

  useEffect(() => {
    if (!isEditMode && currentUser?.role === 'FREELANCE' && currentUser.id) {
      const displayName = authService.getUserDisplayName();
      setReport(prev => ({
        ...prev,
        technicianId: currentUser.id,
        technicianName: displayName
      }));
    }
  }, [isEditMode, currentUser]);

  const handleCustomerSelect = useCallback((customer: Customer) => {
    setReport(prev => ({
      ...prev,
      customerId: customer.id,
      customerName: customer.name,
      selectedCustomer: customer
    }));
    setShowCustomerModal(false);
  }, []);

  const handleTechnicianSelect = useCallback((technician: Technician) => {
    setReport(prev => ({
      ...prev,
      technicianId: technician.id,
      technicianName: technician.name || `${technician.firstName || ''} ${technician.lastName || ''}`.trim(),
      selectedTechnician: technician
    }));
    setShowTechnicianModal(false);
  }, []);

  const handleSupportTechnicianSelect = useCallback((technician: Technician) => {
    setReport(prev => ({
      ...prev,
      supportTechnicianId: technician.id,
      supportTechnicianName: technician.name || `${technician.firstName || ''} ${technician.lastName || ''}`.trim(),
      selectedSupportTechnician: technician
    }));
    setShowSupportTechnicianModal(false);
  }, []);

  const handleClearSupportTechnician = useCallback(() => {
    setReport(prev => ({
      ...prev,
      supportTechnicianId: undefined,
      supportTechnicianName: undefined,
      selectedSupportTechnician: undefined
    }));
  }, []);

  const closeCustomerModal = useCallback(() => {
    setShowCustomerModal(false);
  }, []);

  const closeTechnicianModal = useCallback(() => {
    setShowTechnicianModal(false);
  }, []);

  const closeSupportTechnicianModal = useCallback(() => {
    setShowSupportTechnicianModal(false);
  }, []);

  const isTravelTimeSectionVisible = React.useMemo((): boolean => {
    if (!isEditMode) return false;
    return report.status !== ReportStatus.CANCELED;
  }, [isEditMode, report.status]);

  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!report.customerId) {
      showError(t('messages.selectCustomer'));
      return false;
    }
    if (!report.technicianId) {
      showError(t('messages.selectTechnician'));
      return false;
    }

    const sl = report.serviceLocation;
    const hasDescription = !!(sl?.description?.trim());
    const hasAddress = !!(sl?.address?.trim());
    const hasCity = !!(sl?.city?.trim());
    const hasCountry = !!(sl?.country?.trim());
    const hasAnyTriplet = hasAddress || hasCity || hasCountry;
    if (hasAnyTriplet) {
      if (!hasDescription) { showError(t('messages.serviceLocationDescriptionRequired')); return false; }
      if (!hasAddress) { showError(t('messages.serviceLocationAddressRequired')); return false; }
      if (!hasCity) { showError(t('messages.serviceLocationCityRequired')); return false; }
      if (!hasCountry) { showError(t('messages.serviceLocationCountryRequired')); return false; }
    }

    if (isEditMode && report.id && lastSavedRef.current !== null) {
      if (serializeReportForDirtyCheck(report) === lastSavedRef.current) {
        return true;
      }
    }

    setIsSaving(true);
    try {
      if (isEditMode && report.id) {
        const updatedReport = await apiClient.updateReport(report.id, {
          number: report.number,
          creation: report.creation,
          status: report.status,
          customerId: report.customerId,
          technicianId: report.technicianId,
          supportTechnicianId: report.supportTechnicianId ?? '',
          travelTime: report.travelTime ?? 0,
          travelKm: report.travelKm ?? 0,
          workTime: report.workTime ?? 0,
          startTime: report.startTime,
          finishTime: report.finishTime,
          travelTimeData: report.travelTimeData,
          serviceLocation: report.serviceLocation
        });
        await updateReportCache(updatedReport);
        showSuccess(t('messages.reportUpdatedSuccess'));
        lastSavedRef.current = serializeReportForDirtyCheck(report);
      } else {
        const newReport: ReportRequest = {
          number: nextNumber || report.number || `RPT-${Date.now()}`,
          creation: report.creation || new Date().toISOString(),
          status: ReportStatus.NEW,
          agentId: effectiveAgentId,
          customerId: report.customerId,
          technicianId: report.technicianId,
          supportTechnicianId: report.supportTechnicianId,
          travelTime: report.travelTime ?? 0,
          travelKm: report.travelKm ?? 0,
          workTime: report.workTime ?? 0,
          startTime: report.startTime,
          finishTime: report.finishTime,
          travelTimeData: report.travelTimeData,
          serviceLocation: report.serviceLocation
        };
        const saved = await apiClient.createReport(newReport);
        showSuccess(t('messages.reportCreatedSuccess'));
        navigate(`/report/${saved.id}`);
      }
      return true;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorSavingReport')}: ${message}`);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [report, isEditMode, showError, t, showSuccess, nextNumber, effectiveAgentId, navigate, updateReportCache]);

  /**
   * Calculate totals from machines timelogs and TravelTimeData section.
   * With two technicians, the Travel Time section (outbound/inbound) is made together by both,
   * so its hours count twice; time log travel_time and km are never multiplied.
   */
  const calculatedTotals = React.useMemo(
    () => calculateReportTotals(report.machines, report.travelTimeData, report.supportTechnicianId ? 2 : 1),
    [report.machines, report.travelTimeData, report.supportTechnicianId]
  );

  const handleStatusChange = useCallback(async (newStatus: ReportStatus) => {
    if (!isEditMode || !report.id) return;

    if (newStatus === report.status) return;

    const saved = await handleSave();
    if (!saved) return;

    if (newStatus === ReportStatus.CLOSED) {
      const travelErrors = validateTravelTimeForClose(report.travelTimeData);
      if (travelErrors.length > 0) {
        travelErrors.forEach(({ key, params }) => {
          const legLabel = params?.leg ? t(`travelTime.${params.leg}`) : '';
          showError(t(key, { leg: legLabel }));
        });
        return;
      }
    }

    const statusMessageKeys: Record<ReportStatus, string> = {
      [ReportStatus.NEW]: 'toNew',
      [ReportStatus.READY]: 'toReady',
      [ReportStatus.IN_PROGRESS]: 'toInProgress',
      [ReportStatus.SIGNED]: 'toSigned',
      [ReportStatus.CLOSED]: 'toClosed',
      [ReportStatus.CANCELED]: 'toCanceled'
    };

    const messageKey = statusMessageKeys[newStatus];
    const confirmed = await confirm({
      title: t(`confirmMessages.statusChange.${messageKey}.title`),
      description: t(`confirmMessages.statusChange.${messageKey}.description`)
    });

    if (confirmed) {
      try {
        // T-017: se la transizione e' verso CLOSED, il backend richiede lo snapshot dei totali
        // (obbligatorio solo sui report billing-eligible, ma lo trasmettiamo sempre: e' il backend
        // a decidere se ignorarlo). Stessa calculateReportTotals.ts gia' usata per i totali a video.
        const snapshot = newStatus === ReportStatus.CLOSED
          ? {
              registeredWorkMinutes: calculatedTotals.workTime,
              registeredTravelMinutes: calculatedTotals.travelTime,
              registeredKm: calculatedTotals.travelKm,
            }
          : undefined;
        const updated = await apiClient.updateReportStatus(report.id, newStatus, snapshot);
        // Preserve machines and attachments if not present in the response
        setReport(prev => ({
          ...prev,
          ...updated,
          machines: updated.machines ?? prev.machines,
          attachments: updated.attachments ?? prev.attachments
        }));
        showSuccess(t('messages.statusUpdatedSuccess'));
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        showError(`${t('messages.errorUpdatingStatus')}: ${message}`);
      }
    }
  }, [isEditMode, report.id, report.status, report.travelTimeData, calculatedTotals, confirm, showSuccess, showError, t, handleSave]);

  const handleBeforeNavigate = useCallback(async (_path: string): Promise<boolean> => {
    // TravelTimeSection is editable in SIGNED (readonly only in CLOSED),
    // so we must attempt save unless fully locked (CLOSED or foreign agent).
    if (isEditMode && !isReadonlyForForeignAgent && report.status !== ReportStatus.CLOSED) {
      return handleSave();
    }
    return true;
  }, [isEditMode, isReadonlyForForeignAgent, report.status, handleSave]);

  const handleNavigateWithSave = useCallback(async (path: string) => {
    if (await handleBeforeNavigate(path)) navigate(path);
  }, [handleBeforeNavigate, navigate]);

  const handleDeleteMachine = useCallback(async (e: React.MouseEvent, machineId: string) => {
    e.stopPropagation();

    const confirmed = await confirm({ message: t('confirmMessages.deleteMachine') });
    if (confirmed) {
      try {
        await apiClient.deleteMachine(machineId);
        setReport(prev => ({
          ...prev,
          machines: prev.machines?.filter(m => m.id !== machineId)
        }));
        showSuccess(t('messages.machineDeletedSuccess'));
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        showError(`${t('messages.errorDeletingMachine')}: ${message}`);
      }
    }
  }, [confirm, showSuccess, showError, t]);

  // Attachment handlers
  const handleFileUpload = async (
    file: File,
    type: 'customer' | 'technician' | 'other'
  ) => {
    if (!reportId) return;

    const isSignature = type === 'customer' || type === 'technician';

    let preparedFile = file;
    try {
      if (isSignature) {
        // Signatures must be images and are always aggressively resized so the
        // PDF-generation payload stays small (PROD-463).
        if (!isImageFile(file)) {
          showError(t('messages.fileTooLarge'));
          return;
        }
        preparedFile = await prepareSignatureImage(file);
      } else {
        const result = await validateAndPrepareFile(file);
        preparedFile = result.file;
        if (result.compressed) {
          showSuccess(t('messages.imageCompressed'));
        }
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'fileTooLarge' || message === 'fileTooLargeAfterCompression') {
        showError(t(`messages.${message}`));
      } else {
        showError(`${t('messages.errorUploadingAttachment')}: ${message}`);
      }
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;
      const content = base64.split(',')[1];

      let fileName = preparedFile.name;
      let note = otherNote;

      if (type === 'customer') {
        fileName = 'customer.png';
        note = 'customer_sign';
      } else if (type === 'technician') {
        fileName = 'technician.png';
        note = 'technician_sign';
      }

      try {
        const request: AttachmentRequest = {
          reportId,
          fileName,
          note,
          content,
        };
        await apiClient.addAttachment(request);
        await mutateAttachments();
        showSuccess(t('messages.attachmentUploadedSuccess'));
        setOtherNote('');
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Unknown error';
        showError(`${t('messages.errorUploadingAttachment')}: ${message}`);
      }
    };
    reader.readAsDataURL(preparedFile);
  };

  const handleSignatureFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: 'customer' | 'technician'
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, type);
    }
    event.target.value = '';
  };

  const handleOtherFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, 'other');
    }
    event.target.value = '';
  };

  const handleCanvasSignatureSave = async (
    dataUrl: string,
    type: 'customer' | 'technician'
  ) => {
    if (!reportId) return;

    const fileName = type === 'customer' ? 'customer.png' : 'technician.png';
    const note = type === 'customer' ? 'customer_sign' : 'technician_sign';

    let base64Content: string;
    try {
      // Run canvas signatures through the same resize pipeline as uploads so
      // the PDF-generation payload stays small (PROD-463).
      const preparedFile = await prepareSignatureImage(dataUrlToFile(dataUrl, fileName));
      base64Content = await fileToBase64Content(preparedFile);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorUploadingAttachment')}: ${message}`);
      return;
    }

    try {
      const request: AttachmentRequest = {
        reportId,
        fileName,
        note,
        content: base64Content,
      };
      await apiClient.addAttachment(request);
      await mutateAttachments();
      showSuccess(t('messages.attachmentUploadedSuccess'));

      if (type === 'customer') {
        setShowCustomerSignatureModal(false);
      } else {
        setShowTechnicianSignatureModal(false);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorUploadingAttachment')}: ${message}`);
    }
  };

  const handleQrSignature = useCallback(async () => {
    if (!reportId) return;
    try {
      const authHeader = authService.getAuthorizationHeader();
      if (!authHeader) return;
      const result = await generateSignatureToken(reportId, authHeader);
      setQrSignatureToken({ token: result.token, expiresAt: result.expiresAt });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('qrSignature.generateError')}: ${message}`);
    }
  }, [reportId, showError, t]);

  const handleQrSigned = useCallback(async () => {
    await mutateAttachments();
    setQrSignatureToken(null);
    showSuccess(t('qrSignature.signatureReceived'));
  }, [mutateAttachments, showSuccess, t]);

  const handleDeleteAttachment = useCallback(async (attachmentId: string) => {
    const confirmed = await confirm({ message: t('confirmMessages.deleteAttachment') });
    if (!confirmed) return;

    try {
      await apiClient.deleteAttachment(attachmentId);
      await mutateAttachments();
      if (selectedAttachment?.id === attachmentId) {
        setSelectedAttachment(null);
      }
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('messages.errorDeletingAttachment')}: ${message}`);
    }
  }, [confirm, mutateAttachments, selectedAttachment?.id, showError, t]);

  const handleShowAttachment = useCallback((attachment: Attachment) => {
    if (attachment.fileName && isImageFileName(attachment.fileName)) {
      setPreviewAttachment(attachment);
    } else {
      // PDF or other: download via Blob
      const mimeType = getMimeTypeFromFileName(attachment.fileName || 'file');
      const byteCharacters = atob(attachment.content || '');
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName || 'file';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }, []);

  /**
   * Field readonly based on status and field type
   * - Report Info: E in NEW/READY/IN_PROGRESS, R in SIGNED/CLOSED
   * - Customer Info: E in NEW/READY/IN_PROGRESS, R in SIGNED/CLOSED
   * - Technician Info: E in NEW/READY/IN_PROGRESS, R in SIGNED/CLOSED
   */
  const isFieldReadonly = React.useCallback((_fieldType: string): boolean => {
    if (!isEditMode) return false;
    if (isReadonlyForForeignAgent) return true;

    switch (report.status) {
      case ReportStatus.SIGNED:
      case ReportStatus.CLOSED:
        return true;
      default:
        return false;
    }
  }, [isEditMode, isReadonlyForForeignAgent, report.status]);

  /**
   * Whether customer selection is allowed
   * Editable in NEW/READY/IN_PROGRESS, read-only in SIGNED/CLOSED
   */
  const isCustomerSelectable = React.useMemo((): boolean => {
    if (!isEditMode) return true;
    if (isReadonlyForForeignAgent) return false;
    return report.status === ReportStatus.NEW || report.status === ReportStatus.READY || report.status === ReportStatus.IN_PROGRESS;
  }, [isEditMode, isReadonlyForForeignAgent, report.status]);

  /**
   * Whether technician selection is allowed.
   * FREELANCE users always have a fixed technician (themselves) — selection is never shown.
   * For others: editable in NEW/READY/IN_PROGRESS, read-only in SIGNED/CLOSED.
   */
  const isTechnicianSelectable = React.useMemo((): boolean => {
    if (currentUser?.role === 'FREELANCE') return false;
    if (!isEditMode) return true;
    if (isReadonlyForForeignAgent) return false;
    return report.status === ReportStatus.NEW || report.status === ReportStatus.READY || report.status === ReportStatus.IN_PROGRESS;
  }, [isEditMode, isReadonlyForForeignAgent, report.status, currentUser?.role]);

  /**
   * Whether signatures section is visible
   * Hidden in NEW/READY, visible in IN_PROGRESS/SIGNED/CLOSED
   */
  const isSignaturesSectionVisible = React.useMemo((): boolean => {
    if (!isEditMode) return false;
    return report.status === ReportStatus.IN_PROGRESS ||
      report.status === ReportStatus.SIGNED ||
      report.status === ReportStatus.CLOSED;
  }, [isEditMode, report.status]);

  /**
   * Status color mapping
   */
  const getStatusColor = React.useCallback((status: string | undefined): string => {
    switch (status) {
      case ReportStatus.NEW:
        return 'var(--color-status-new)';
      case ReportStatus.READY:
        return 'var(--color-status-ready)';
      case ReportStatus.IN_PROGRESS:
        return 'var(--color-status-in-progress)';
      case ReportStatus.SIGNED:
        return 'var(--color-status-signed)';
      case ReportStatus.CLOSED:
        return 'var(--color-status-closed)';
      case ReportStatus.CANCELED:
        return 'var(--color-status-canceled)';
      default:
        return 'var(--color-bg-page)';
    }
  }, []);

  /**
   * Returns valid status transitions based on current status.
   * Transitions: NEW→READY, READY→IN_PROGRESS, IN_PROGRESS→SIGNED, SIGNED→CLOSED
   * Cancel: NEW→CANCELED, READY→CANCELED
   */
  const validStatusTransitions = React.useMemo((): ReportStatus[] => {
    switch (report.status) {
      case ReportStatus.NEW:
        return [ReportStatus.NEW, ReportStatus.READY, ReportStatus.CANCELED];
      case ReportStatus.READY:
        return [ReportStatus.READY, ReportStatus.IN_PROGRESS, ReportStatus.CANCELED];
      case ReportStatus.IN_PROGRESS:
        return [ReportStatus.IN_PROGRESS, ReportStatus.SIGNED];
      case ReportStatus.SIGNED:
        return [ReportStatus.SIGNED, ReportStatus.CLOSED];
      case ReportStatus.CLOSED:
        return [ReportStatus.CLOSED]; // Terminal state
      case ReportStatus.CANCELED:
        return [ReportStatus.CANCELED]; // Terminal state
      default:
        return [ReportStatus.NEW];
    }
  }, [report.status]);

  /**
   * Whether status change is disabled (terminal states)
   */
  const isStatusChangeDisabled = React.useMemo(
    (): boolean => isReadonlyForForeignAgent || report.status === ReportStatus.CLOSED || report.status === ReportStatus.CANCELED,
    [isReadonlyForForeignAgent, report.status]
  );

  /**
   * Whether form is in readonly mode (SIGNED or CLOSED)
   */
  const isFormReadonly = React.useMemo(
    (): boolean => isReadonlyForForeignAgent || report.status === ReportStatus.SIGNED || report.status === ReportStatus.CLOSED,
    [isReadonlyForForeignAgent, report.status]
  );

  /**
   * Service Location is editable only in NEW/READY (and on creation).
   * From IN_PROGRESS onward it becomes read-only.
   */
  const isServiceLocationEditable = React.useMemo((): boolean => {
    if (!isEditMode) return true;
    if (isReadonlyForForeignAgent) return false;
    return report.status === ReportStatus.NEW || report.status === ReportStatus.READY;
  }, [isEditMode, isReadonlyForForeignAgent, report.status]);

  const isTravelTimeReadonly = React.useMemo((): boolean => {
    if (isReadonlyForForeignAgent) return true;
    return report.status === ReportStatus.CLOSED;
  }, [isReadonlyForForeignAgent, report.status]);


  const formatDateOnly = (isoString: string | undefined): string => {
    if (!isoString) return '';
    try {
      return new Date(isoString).toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  const handleGeneratePdf = useCallback(async () => {
    if (!isEditMode || !loadedReport) return;

    setIsGeneratingPdf(true);
    try {
      const reportForMapping: Report = { ...loadedReport, machines: report.machines };
      const dto = mapReportToUbaldoDTO(
        reportForMapping,
        editModeCustomer ?? null,
        agent,
        calculatedTotals.workTime,
        calculatedTotals.travelTime,
        calculatedTotals.travelKm,
        customerSignature?.content,
        technicianSignature?.content,
        customerSignature?.updatedAt || customerSignature?.createdAt
      );
      const language = i18n.language.toUpperCase().startsWith('IT') ? 'IT' : 'EN';
      const blob = await generateReportPdf(dto, language);
      downloadBlob(blob, `report-${report.number || 'download'}.pdf`);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      showError(`${t('reportForm.downloadPdf')}: ${message}`);
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [isEditMode, loadedReport, report.machines, report.number, editModeCustomer, agent, calculatedTotals, customerSignature, technicianSignature, showError, t, i18n.language]);

  // Se si arriva dal dettaglio fatturazione (a sua volta raggiunto dalla lista fatturazione),
  // il primo passo della breadcrumb deve tornare li' e non alla dashboard standard - altrimenti
  // l'utente perde il filo di dove si trovava prima di aprire il report.
  const cameFromBillingList = (location.state as { from?: string } | null)?.from === 'billing-list';
  const isFinance = currentUser?.role === 'FINANCE';
  // Feedback ambiente di test 23/09/2026: il punto di accesso alla fatturazione non deve comparire
  // per TECHNICIAN e FREELANCE - stessa logica di canAccessBilling in MainForm.tsx.
  const canAccessBillingSection = currentUser?.role !== 'TECHNICIAN' && currentUser?.role !== 'FREELANCE';
  const homeBreadcrumbItem: BreadcrumbItem = cameFromBillingList
    ? { label: '', translateKey: 'breadcrumb.financeHome', path: isFinance ? '/' : '/billing', onClick: handleBeforeNavigate }
    : { label: '', translateKey: 'breadcrumb.dashboard', path: getDashboardPath(currentUser), onClick: handleBeforeNavigate };

  const breadcrumbItems: BreadcrumbItem[] = [
    homeBreadcrumbItem,
    {
      label: isEditMode
        ? `${t('breadcrumb.report')} #${report.number || ''}`
        : t('breadcrumb.newReport')
    }
  ];

  if (loading) {
    return <div className="loading">{t('app.loading')}</div>;
  }

  return (
    <div className="report-form">
      <NotificationContainer />
      <ConfirmDialog />
      <FormHeader breadcrumbItems={breadcrumbItems}>
        <div className="header-actions">
          <button
            onClick={handleGeneratePdf}
            className={`btn-download-pdf ${isGeneratingPdf ? 'btn-loading' : ''}`}
            disabled={!isEditMode || isGeneratingPdf}
          >
            {isGeneratingPdf ? (
              <><span className="btn-spinner"></span> {t('reportForm.downloadPdf')}</>
            ) : (
              <><span className="material-icons">picture_as_pdf</span> {t('reportForm.downloadPdf')}</>
            )}
          </button>
          <button onClick={handleSave} className={`btn-save ${isSaving ? 'btn-loading' : ''}`} disabled={isSaving || isReadonlyForForeignAgent}>
            {isSaving ? (
              <><span className="btn-spinner"></span> {t('common.saving')}</>
            ) : (
              <><span className="material-icons">save</span> {isEditMode ? t('common.update') : t('common.save')}</>
            )}
          </button>
        </div>
      </FormHeader>

      <div className="form-layout">
        {/* Main Content */}
        <div className="form-main-content">
          {/* Report Info */}
          <section className="form-section">
            <h3>{t('reportForm.reportInfo')}</h3>
            <div className="form-grid">
              <div className="form-field">
                <label>{t('reportForm.startDate')}</label>
                <input
                  type="date"
                  value={formatDateOnly(report.startTime)}
                  onChange={(e) => setReport({ ...report, startTime: new Date(e.target.value).toISOString() })}
                  disabled={isFieldReadonly('dates')}
                />
              </div>
              <div className="form-field">
                <label>{t('reportForm.finishDate')}</label>
                <input
                  type="date"
                  value={formatDateOnly(report.finishTime)}
                  onChange={(e) => setReport({ ...report, finishTime: new Date(e.target.value).toISOString() })}
                  disabled={isFieldReadonly('dates')}
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-field">
                <label>{t('reportForm.estimatedWorkTime')}</label>
                <input
                  type="number"
                  value={report.workTime || 0}
                  onChange={(e) => setReport({ ...report, workTime: parseInt(e.target.value) })}
                  disabled={isFieldReadonly('estimates')}
                />
              </div>
              <div className="form-field">
                <label>{t('reportForm.estimatedTravelTime')}</label>
                <input
                  type="number"
                  value={report.travelTime || 0}
                  onChange={(e) => setReport({ ...report, travelTime: parseInt(e.target.value) })}
                  disabled={isFieldReadonly('estimates')}
                />
              </div>
              <div className="form-field">
                <label>{t('reportForm.estimatedTravelKm')}</label>
                <input
                  type="number"
                  value={report.travelKm || 0}
                  onChange={(e) => setReport({ ...report, travelKm: parseInt(e.target.value) })}
                  disabled={isFieldReadonly('estimates')}
                />
              </div>
            </div>

            {/* Calculated totals from machines timelogs */}
            {isEditMode && (
              <>
                <div className="form-grid calculated-totals">
                  <div className="form-field">
                    <label>{t('reportForm.calculatedWorkTime')}</label>
                    <input
                      type="text"
                      value={formatMinutesToHoursMinutes(calculatedTotals.workTime)}
                      readOnly
                      className="input-readonly calculated-value"
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('reportForm.calculatedTravelTime')}</label>
                    <input
                      type="text"
                      value={formatMinutesToHoursMinutes(calculatedTotals.travelTime)}
                      readOnly
                      className="input-readonly calculated-value"
                    />
                  </div>
                  <div className="form-field">
                    <label>{t('reportForm.calculatedTravelKm')}</label>
                    <input
                      type="text"
                      value={`${calculatedTotals.travelKm} km`}
                      readOnly
                      className="input-readonly calculated-value"
                    />
                  </div>
                </div>
                <p className="calculated-note">{t('reportForm.calculatedFromTimelogs')}</p>
              </>
            )}
          </section>

          {/* Travel Time Section */}
          {isTravelTimeSectionVisible && (
            <TravelTimeSection
              data={report.travelTimeData || {}}
              onChange={(data) => setReport(prev => ({ ...prev, travelTimeData: data }))}
              readonly={isTravelTimeReadonly}
            />
          )}

          {/* Technicians Section */}
          <section className="form-section">
            <div className="section-header">
              <h3>{t('reportForm.techniciansInfo')}</h3>
            </div>
            <div className="form-grid form-grid-2col">
              <div className="form-field">
                <div className="field-header">
                  <label>{t('reportForm.technicianName')}</label>
                  {isTechnicianSelectable && (
                    <button
                      onClick={() => setShowTechnicianModal(true)}
                      className="btn-select-inline"
                      data-testid="select-technician-btn"
                    >
                      {t('common.select')}
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={report.technicianName || report.selectedTechnician?.name || ''}
                  readOnly
                  className="input-readonly"
                />
              </div>
              <div className="form-field">
                <div className="field-header">
                  <label>{t('reportForm.supportTechnicianName')}</label>
                  {isTechnicianSelectable && (
                    <div className="field-header-buttons">
                      <button
                        onClick={() => setShowSupportTechnicianModal(true)}
                        className="btn-select-inline"
                        data-testid="select-support-technician-btn"
                      >
                        {t('common.select')}
                      </button>
                      {report.supportTechnicianId && (
                        <button
                          onClick={handleClearSupportTechnician}
                          className="btn-clear-inline"
                          title={t('common.clear')}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <input
                  type="text"
                  value={report.supportTechnicianName || report.selectedSupportTechnician?.name || t('reportForm.noSupportTechnician')}
                  readOnly
                  className="input-readonly"
                />
              </div>
            </div>
          </section>

          {/* Machines Section - Only in edit mode */}
          {isEditMode && (
            <section className="form-section">
              <div className="section-header">
                <h3>{t('reportForm.machines')}</h3>
                {!isFormReadonly && (
                  <>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      <button
                        onClick={() => setShowMultiMachineCsvModal(true)}
                        className="btn-import-csv"
                      >
                        <span className="material-icons">upload_file</span> {t('reportForm.importCsv')}
                      </button>
                      <CsvImportTooltip variant="multi" />
                    </div>
                    <button
                      onClick={() => setShowPantheraImportModal(true)}
                      className="btn-import-csv"
                    >
                      <span className="material-icons">cloud_download</span> {t('reportForm.importPanthera')}
                    </button>
                    <button
                      onClick={() => navigate(`/report/${id}/machine/new`)}
                      className="btn-add"
                    >
                      <span className="material-icons">add</span> {t('reportForm.addMachine')}
                    </button>
                  </>
                )}
              </div>
              <div className="machines-list">
                <table className="machines-table">
                  <thead>
                    <tr>
                      <th>{t('machineForm.idNumber')}</th>
                      <th>{t('machineForm.description')}</th>
                      <th>{t('machineForm.serviceType')}</th>
                      {report.status === ReportStatus.CLOSED && <th>{t('reportForm.failureClassification')}</th>}
                      {!isFormReadonly && <th>{t('common.actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {report.machines && report.machines.length > 0 ? (
                      report.machines.map((machine) => (
                        <tr
                          key={machine.id}
                          onClick={() => handleNavigateWithSave(`/report/${id}/machine/${machine.id}`)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{machine.idNumber}</td>
                          <td>{machine.description}</td>
                          <td><span className="machine-service">{t(`serviceType.${machine.serviceType}`)}</span></td>
                          {report.status === ReportStatus.CLOSED && (
                            <td>
                              {machine.failureClassifications && machine.failureClassifications.length > 0 ? (
                                <div className="machine-failures">
                                  {machine.failureClassifications.map((f, index) => {
                                    const items = getFailureDisplayItems(f, i18n.language);
                                    return (
                                      <span key={index} className="failure-badge">
                                        {items.length > 0 ? (
                                          items.map((item, i) => (
                                            <React.Fragment key={i}>
                                              <span title={item.raw ?? undefined}>{item.label}</span>
                                              {i < items.length - 1 ? ' · ' : ''}
                                            </React.Fragment>
                                          ))
                                        ) : (
                                          getUnclassifiedFailureLabel(machine.serviceType, t)
                                        )}
                                      </span>
                                    );
                                  })}
                                </div>
                              ) : (
                                <span className="no-data-inline">{getUnclassifiedFailureLabel(machine.serviceType, t)}</span>
                              )}
                            </td>
                          )}
                          {!isFormReadonly && (
                            <td className="actions-cell">
                              <button
                                onClick={(e) => handleDeleteMachine(e, machine.id)}
                                className="btn-action btn-delete"
                                title={t('common.delete')}
                              >
                                <span className="material-icons">delete</span>
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={(report.status === ReportStatus.CLOSED ? 1 : 0) + (isFormReadonly ? 3 : 4)} className="no-data">{t('reportForm.noMachines')}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {/* Signatures Section - Hidden in NEW/READY, visible in IN_PROGRESS/SIGNED/CLOSED */}
          {isSignaturesSectionVisible && (
            <section className="form-section">
              <div className="section-header">
                <h3>{t('attachmentsForm.signatures')}</h3>
              </div>
              <table className="attachments-table">
                <thead>
                  <tr>
                    <th>{t('attachmentsForm.fileName')}</th>
                    <th>{t('attachmentsForm.note')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Customer row — always visible */}
                  <tr>
                    <td>{customerSignature ? customerSignature.fileName : '—'}</td>
                    <td>{t('attachmentsForm.customerSignature')}</td>
                    <td className="actions-cell sig-actions-cell">
                      {customerSignature ? (
                        <>
                          <button
                            onClick={() => handleShowAttachment(customerSignature)}
                            className="btn-action btn-show"
                            title={t('common.show')}
                          >
                            <span className="material-icons">visibility</span>
                          </button>
                          {!isFormReadonly && (
                            <button
                              onClick={() => handleDeleteAttachment(customerSignature.id)}
                              className="btn-action btn-delete"
                              title={t('common.delete')}
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          )}
                        </>
                      ) : !isFormReadonly ? (
                        <>
                          {(SIGNATURE_METHOD === SignatureMethod.UPLOAD_SIGNATURE ||
                            SIGNATURE_METHOD === SignatureMethod.UPLOAD_AND_CANVAS) && (
                              <button
                                onClick={() => customerSignFileRef.current?.click()}
                                className="btn-signature btn-customer"
                                title={t('attachmentsForm.uploadCustomerSignature')}
                              >
                                <span className="material-icons">upload</span>
                                {t('attachmentsForm.uploadCustomerSignature')}
                              </button>
                            )}
                          {(SIGNATURE_METHOD === SignatureMethod.CANVAS_SIGNATURE ||
                            SIGNATURE_METHOD === SignatureMethod.UPLOAD_AND_CANVAS) && (
                              <button
                                onClick={() => setShowCustomerSignatureModal(true)}
                                className="btn-signature btn-customer btn-draw"
                                title={t('attachmentsForm.drawCustomerSignature')}
                              >
                                <span className="material-icons">draw</span>
                                {t('attachmentsForm.drawCustomerSignature')}
                              </button>
                            )}
                          <button
                            onClick={handleQrSignature}
                            className="btn-signature btn-customer btn-qr"
                            title={t('qrSignature.buttonTitle')}
                          >
                            <span className="material-icons">qr_code</span>
                            {t('qrSignature.button')}
                          </button>
                        </>
                      ) : '—'}
                    </td>
                  </tr>
                  {/* Technician row — always visible */}
                  <tr>
                    <td>{technicianSignature ? technicianSignature.fileName : '—'}</td>
                    <td>{t('attachmentsForm.technicianSignature')}</td>
                    <td className="actions-cell sig-actions-cell">
                      {technicianSignature ? (
                        <>
                          <button
                            onClick={() => handleShowAttachment(technicianSignature)}
                            className="btn-action btn-show"
                            title={t('common.show')}
                          >
                            <span className="material-icons">visibility</span>
                          </button>
                          {!isFormReadonly && (
                            <button
                              onClick={() => handleDeleteAttachment(technicianSignature.id)}
                              className="btn-action btn-delete"
                              title={t('common.delete')}
                            >
                              <span className="material-icons">delete</span>
                            </button>
                          )}
                        </>
                      ) : !isFormReadonly ? (
                        <>
                          {(SIGNATURE_METHOD === SignatureMethod.UPLOAD_SIGNATURE ||
                            SIGNATURE_METHOD === SignatureMethod.UPLOAD_AND_CANVAS) && (
                              <button
                                onClick={() => technicianSignFileRef.current?.click()}
                                className="btn-signature btn-technician"
                                title={t('attachmentsForm.uploadTechnicianSignature')}
                              >
                                <span className="material-icons">upload</span>
                                {t('attachmentsForm.uploadTechnicianSignature')}
                              </button>
                            )}
                          {(SIGNATURE_METHOD === SignatureMethod.CANVAS_SIGNATURE ||
                            SIGNATURE_METHOD === SignatureMethod.UPLOAD_AND_CANVAS) && (
                              <button
                                onClick={() => setShowTechnicianSignatureModal(true)}
                                className="btn-signature btn-technician btn-draw"
                                title={t('attachmentsForm.drawTechnicianSignature')}
                              >
                                <span className="material-icons">draw</span>
                                {t('attachmentsForm.drawTechnicianSignature')}
                              </button>
                            )}
                        </>
                      ) : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
              {/* Hidden file inputs for signatures */}
              <input
                ref={customerSignFileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleSignatureFileChange(e, 'customer')}
                style={{ display: 'none' }}
                data-testid="customer-signature-file-input"
              />
              <input
                ref={technicianSignFileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={(e) => handleSignatureFileChange(e, 'technician')}
                style={{ display: 'none' }}
                data-testid="technician-signature-file-input"
              />
            </section>
          )}

          {/* Other Attachments Section - Only in edit mode */}
          {isEditMode && (
            <section className="form-section">
              <div className="section-header">
                <div className="section-title-row">
                  <h3>{t('reportForm.attachments')}</h3>
                  <InfoTooltip text={
                    <>
                      <strong>{t('reportForm.attachmentsTooltipTitle')}</strong>
                      <ul>
                        <li>{t('reportForm.attachmentsTooltipImages')}</li>
                        <li>{t('reportForm.attachmentsTooltipFiles')}</li>
                      </ul>
                    </>
                  } />
                </div>
                <div className="attachment-add-controls">
                  <input
                    type="text"
                    placeholder={t('attachmentsForm.note')}
                    value={otherNote}
                    onChange={(e) => setOtherNote(e.target.value)}
                    className="note-input"
                  />
                  <button
                    onClick={() => otherFileRef.current?.click()}
                    className="btn-add-other"
                    disabled={isFormReadonly}
                  >
                    <span className="material-icons">add</span> {t('attachmentsForm.otherAttachment')}
                  </button>
                </div>
              </div>
              <table className="attachments-table">
                <thead>
                  <tr>
                    <th>{t('attachmentsForm.fileName')}</th>
                    <th>{t('attachmentsForm.note')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {otherAttachments.length > 0 ? (
                    otherAttachments.map((attachment) => {
                      const isImage = isImageFileName(attachment.fileName || '');
                      const isZip = isZipFileName(attachment.fileName || '');
                      const iconClass = isImage ? 'icon-image' : isZip ? 'icon-zip' : 'icon-pdf';
                      const iconName = isImage ? 'image' : isZip ? 'folder_zip' : 'picture_as_pdf';
                      return (
                        <tr key={attachment.id}>
                          <td>
                            <div className="attachment-filename-cell">
                              <span className={`material-icons attachment-type-icon ${iconClass}`}>
                                {iconName}
                              </span>
                              {attachment.fileName}
                            </div>
                          </td>
                          <td>{attachment.note}</td>
                          <td className="actions-cell">
                            {isImage ? (
                              <button
                                onClick={() => handleShowAttachment(attachment)}
                                className="btn-action btn-show"
                                title={t('attachmentsForm.preview')}
                              >
                                <span className="material-icons">visibility</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleShowAttachment(attachment)}
                                className="btn-action btn-download"
                                title={t('attachmentsForm.download')}
                              >
                                <span className="material-icons">download</span>
                              </button>
                            )}
                            {!isFormReadonly && (
                              <button
                                onClick={() => handleDeleteAttachment(attachment.id)}
                                className="btn-action btn-delete"
                                title={t('common.delete')}
                              >
                                <span className="material-icons">delete</span>
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={3} className="no-data">{t('attachmentsForm.noAttachments')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
              {/* Hidden file input for other attachments */}
              <input
                ref={otherFileRef}
                type="file"
                accept="image/*,application/pdf,application/zip,application/x-rar-compressed,application/x-7z-compressed,.zip,.rar,.7z,.tar,.gz"
                onChange={handleOtherFileChange}
                style={{ display: 'none' }}
                data-testid="other-attachment-file-input"
              />
            </section>
          )}
        </div>

        {/* Sidebar - Right Column */}
        <aside className="form-sidebar">
          {/* Report Status Section */}
          {isEditMode && (
            <section className="form-section sidebar-section">
              <div className="section-header-with-tooltip">
                <h3>{t('reportForm.reportStatus')}</h3>
                <StatusFlowTooltip currentStatus={report.status as ReportStatus} />
              </div>
              <div className="form-grid-sidebar">
                <div className="form-field">
                  <label>{t('reportForm.status')}</label>
                  <select
                    aria-label="Report Status"
                    value={report.status || ReportStatus.NEW}
                    onChange={(e) => handleStatusChange(e.target.value as ReportStatus)}
                    disabled={isStatusChangeDisabled}
                    className="status-select"
                    style={{
                      backgroundColor: getStatusColor(report.status),
                      color: '#000',
                      fontWeight: 'bold',
                      padding: '8px',
                      border: '2px solid #ccc',
                      borderRadius: '4px'
                    }}
                  >
                    {validStatusTransitions.map((status) => (
                      <option key={status} value={status}>
                        {t(`status.${status}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>
          )}

          {/* T-017: Fatturazione, sotto Report Status */}
          {isEditMode && canAccessBillingSection && (
            <section className="form-section sidebar-section">
              <h3>{t('billing.sectionTitle')}</h3>
              <button
                type="button"
                className="btn-primary btn-billing"
                onClick={() => navigate(`/report/${report.id}/billing`)}
              >
                <span className="material-icons">receipt_long</span>
                {t('billing.openPage')}
                <BillingStatusBadge billingStatus={report.billingStatus} />
              </button>
            </section>
          )}

          {/* Customer Section */}
          <CustomerInfo
            customer={isEditMode ? (report.selectedCustomer ?? editModeCustomer) : report.selectedCustomer}
            customerName={report.customerName}
            showDetails={!!(isEditMode ? (report.selectedCustomer ?? editModeCustomer) : report.selectedCustomer)}
            showSelectButton={isCustomerSelectable}
            onSelectCustomer={() => setShowCustomerModal(true)}
          />

          {/* Service Location Section */}
          <ServiceLocationSection
            serviceLocation={report.serviceLocation}
            onChange={(sl) => setReport(prev => ({ ...prev, serviceLocation: sl }))}
            editable={isServiceLocationEditable}
          />
        </aside>
      </div >

      {/* Modals */}
      {showMultiMachineCsvModal && id && (
        <MultiMachineCsvImportModal
          reportId={id}
          onClose={() => {
            setShowMultiMachineCsvModal(false);
            refreshReport();
          }}
        />
      )}
      {showPantheraImportModal && id && (
        <PantheraImportModal
          reportId={id}
          onClose={() => {
            setShowPantheraImportModal(false);
            refreshReport();
          }}
          onSwitchToCsv={() => {
            setShowPantheraImportModal(false);
            setShowMultiMachineCsvModal(true);
          }}
        />
      )}
      {showCustomerModal && (
        <CustomerSelectionModal
          onSelect={handleCustomerSelect}
          onClose={closeCustomerModal}
        />
      )}
      {showTechnicianModal && (
        <TechnicianSelectionModal
          onSelect={handleTechnicianSelect}
          onClose={closeTechnicianModal}
        />
      )}
      {showSupportTechnicianModal && (
        <TechnicianSelectionModal
          onSelect={handleSupportTechnicianSelect}
          onClose={closeSupportTechnicianModal}
        />
      )}
      {showCustomerSignatureModal && (
        <SignatureModal
          title={t('signatureModal.customerSignatureTitle')}
          onSave={(dataUrl) => handleCanvasSignatureSave(dataUrl, 'customer')}
          onClose={() => setShowCustomerSignatureModal(false)}
        />
      )}
      {showTechnicianSignatureModal && (
        <SignatureModal
          title={t('signatureModal.technicianSignatureTitle')}
          onSave={(dataUrl) => handleCanvasSignatureSave(dataUrl, 'technician')}
          onClose={() => setShowTechnicianSignatureModal(false)}
        />
      )}
      {qrSignatureToken && (
        <QrSignatureModal
          token={qrSignatureToken.token}
          expiresAt={qrSignatureToken.expiresAt}
          onClose={() => setQrSignatureToken(null)}
          onSigned={handleQrSigned}
        />
      )}
      {previewAttachment && (
        <ImagePreviewModal
          fileName={previewAttachment.fileName || ''}
          base64Content={previewAttachment.content || ''}
          mimeType={getMimeTypeFromFileName(previewAttachment.fileName || '')}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
    </div >
  );
};

export default ReportForm;
